import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Divider, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, FavoriteBorder as FavoriteBorderIcon } from '@mui/icons-material';
import { Product } from '../../types';
import Button from '../common/Button';
import PriceDisplay from '../common/PriceDisplay';
import { APP_NAME } from '../../constants';
import { resolveImageUrl } from '../../utils/image';
import { isPromoActive } from '../../utils/pricing';
import TrustBadges from '../common/TrustBadges';

interface ProductDetailProps {
  product: Product;
  onAddToCart: () => void;
  onGoToCart: () => void;
  onBuyNow?: () => void;
  onToggleWishlist: () => void;
  isWishlisted?: boolean;
  isInCart?: boolean;
}

const ProductDetail: React.FC<ProductDetailProps> = ({
  product,
  onAddToCart,
  onGoToCart,
  onBuyNow,
  onToggleWishlist,
  isWishlisted = false,
  isInCart = false
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const primaryImage =
    product.images[activeImageIndex] || product.images[0] || '';

  const hasMultipleImages = product.images.length > 1;

  useEffect(() => {
    if (!hasMultipleImages) return;

    const interval = setInterval(() => {
      setActiveImageIndex((prev) =>
        prev + 1 < product.images.length ? prev + 1 : 0
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [hasMultipleImages, product.images.length]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.2fr' },
        gap: 4
      }}
    >
      <Box>
        <Box
          sx={{
            width: '100%',
            height: { xs: 360, md: 520 },
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
            mb: 2,
            position: 'relative',
            bgcolor: '#f7f7f8'
          }}
        >
          <Box
            component="img"
            src={resolveImageUrl(primaryImage)}
            alt={product.name}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />

          {hasMultipleImages && (
            <>
              <IconButton
                size="small"
                onClick={() =>
                  setActiveImageIndex((prev) =>
                    prev - 1 >= 0 ? prev - 1 : product.images.length - 1
                  )
                }
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.8)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  setActiveImageIndex((prev) =>
                    prev + 1 < product.images.length ? prev + 1 : 0
                  )
                }
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.8)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                }}
              >
                <ChevronRight />
              </IconButton>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.7
                }}
              >
                {product.images.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor:
                        index === activeImageIndex
                          ? 'primary.main'
                          : 'rgba(255,255,255,0.8)',
                      border:
                        index === activeImageIndex
                          ? '1px solid rgba(0,0,0,0.1)'
                          : '1px solid transparent'
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>

        {product.images.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              pb: 1
            }}
          >
            {product.images.map((img, index) => (
              <Box
                key={img + index}
                component="img"
                src={resolveImageUrl(img)}
                alt={`${product.name} ${index + 1}`}
                onClick={() => setActiveImageIndex(index)}
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 2,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border:
                    index === activeImageIndex
                      ? '2px solid'
                      : '1px solid transparent',
                  borderColor:
                    index === activeImageIndex ? 'primary.main' : 'divider',
                  boxShadow:
                    index === activeImageIndex
                      ? '0 0 0 2px rgba(227,91,143,0.2)'
                      : 'none'
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ textTransform: 'uppercase', letterSpacing: '0.12em', mb: 1 }}>
          {APP_NAME}
        </Typography>
        <Typography variant="h3" sx={{ mb: 1.5 }}>
          {product.name}
        </Typography>
        {product.category && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Category: {product.category.name}
          </Typography>
        )}
        <Typography variant="body1" sx={{ mb: 3 }}>
          {product.description}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          {product.isEarring && (
            <Chip label="Earring" size="small" sx={{ bgcolor: '#ffe6f0', color: 'text.primary' }} />
          )}
          {product.material && (
            <Chip label={product.material} size="small" sx={{ bgcolor: '#f2f0ff', color: 'text.primary' }} />
          )}
          {product.style?.map((s) => (
            <Chip key={s} label={s} size="small" sx={{ bgcolor: '#e6f5ff', color: 'text.primary' }} />
          ))}
          {product.colors?.map((c) => (
            <Chip key={c} label={c} size="small" sx={{ bgcolor: '#f9f1ff', color: 'text.primary' }} />
          ))}
        </Box>

        <PriceDisplay item={product} priceVariant="h4" mrpVariant="body1" sx={{ mb: 1 }} />
        {isPromoActive(product) && product.promoEndsAt && (
          <Typography variant="body2" color="error.main" sx={{ mb: 1, fontWeight: 600 }}>
            Limited-time price — sale ends{' '}
            {new Date(product.promoEndsAt).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'short'
            })}
          </Typography>
        )}
        <Typography
          variant="body2"
          color={product.stock <= 0 ? 'error.main' : product.stock <= 5 ? 'warning.main' : 'success.main'}
          sx={{ mb: 3, fontWeight: product.stock <= 5 && product.stock > 0 ? 600 : 400 }}
        >
          {product.stock <= 0
            ? 'Out of stock'
            : product.stock <= 5
              ? `Only ${product.stock} left — order soon!`
              : 'In stock'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
          {product.stock > 0 && onBuyNow && (
            <Button
              variant="contained"
              color="primary"
              onClick={onBuyNow}
            >
              Buy Now
            </Button>
          )}
          <Button
            variant={onBuyNow ? 'outlined' : 'contained'}
            color="primary"
            disabled={!isInCart && product.stock <= 0}
            onClick={isInCart ? onGoToCart : onAddToCart}
          >
            {isInCart ? 'Go to Cart' : product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
          </Button>
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<FavoriteBorderIcon />}
          onClick={onToggleWishlist}
          sx={{ ml: 1.5 }}
        >
          {isWishlisted ? 'Wishlisted' : 'Wishlist'}
        </Button>

        <Box sx={{ mt: 3 }}>
          <TrustBadges />
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Product details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          SKU: {product.sku}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Weight: {product.weight} g
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Size: {product.dimensions.length} × {product.dimensions.width} × {product.dimensions.height} mm
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductDetail;

